import { Box, Button, IconButton, Typography } from "@mui/material";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import LinkIcon from "@mui/icons-material/Link";
import { useState } from "react";
import { api } from "../../../api/API";
import { BulkInput, Task } from "../../../api/Tasks";
import { Dropdown } from "../../../misc/Dropdown";
import { FieldRow } from "../../../misc/FieldRow";
import { useSnackAction } from "../../../misc/SnackAction";
import { TinyButton } from "../../../misc/TinyButton";
import { ProjectRef, TaskProject } from "../panel/TaskProject";
import { taskPermalink } from "../TaskPicker";
import { BulkDueDateButton } from "./BulkDueDateButton";
import { BulkTaskLinks } from "./BulkTaskLinks";

type Archive = "" | "archive";
type Completed = "" | "complete" | "open";

const archiveOptions: { value: Archive; label: string }[] = [
    { value: "", label: "No change" },
    { value: "archive", label: "Archive" },
];

const completedOptions: { value: Completed; label: string }[] = [
    { value: "", label: "No change" },
    { value: "complete", label: "Mark complete" },
    { value: "open", label: "Mark not complete" },
];

// BulkTaskPanel edits many tasks at once. Each field starts at "no change", and only
// the fields that were set are applied.
export function BulkTaskPanel(props: {
    tasks: Task[];
    onClose: () => void;

    // onApplied runs after the change was saved
    onApplied: () => void;
}) {
    const [archive, setArchive] = useState<Archive>("");
    const [completed, setCompleted] = useState<Completed>("");

    // undefined is no change, null takes the value off
    const [dueDate, setDueDate] = useState<string | null | undefined>(undefined);
    const [project, setProject] = useState<ProjectRef | null | undefined>(undefined);

    const [blockedBy, setBlockedBy] = useState<Task[]>([]);
    const [followedBy, setFollowedBy] = useState<Task[]>([]);

    const ids = props.tasks.map(task => task.id);

    // a project can only hold tasks of its own team, so the picker offers that team's
    // projects when the tasks share one
    const teams = Array.from(new Set(props.tasks.map(task => task.team)));
    const team = teams.length === 1 ? teams[0] : 0;

    const input: BulkInput = {
        ids: ids,
        archived: archive === "archive" ? true : undefined,
        completed: completed === "" ? undefined : completed === "complete",
        setDueDate: dueDate !== undefined,
        dueDate: dueDate,
        setProject: project !== undefined,
        project: project ? project.id : null,
        goal: project && project.goal ? project.goal : null,
        blockedBy: blockedBy.map(task => task.id),
        followedBy: followedBy.map(task => task.id),
    };

    const changed = !!archive || !!completed || dueDate !== undefined || project !== undefined
        || blockedBy.length > 0 || followedBy.length > 0;

    const apply = useSnackAction(async () => {
        await api.tasks.bulk(input);
        props.onApplied();
    }, { success: "Saved" });

    const copyLinks = useSnackAction(async () => {
        const text = props.tasks.map(task => task.title + " - " + taskPermalink(task)).join("\n");
        await navigator.clipboard.writeText(text);
    }, { success: "Links copied" });

    return (
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minWidth: 0, minHeight: 0 }}>
            <Box sx={{
                display: "flex", alignItems: "center", gap: 1, flexShrink: 0,
                pl: 3, pr: 1, py: 1,
                bgcolor: "grey.50",
                borderBottom: 1, borderColor: "divider",
            }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                    {props.tasks.length} tasks selected
                </Typography>

                <TinyButton color="primary" startIcon={<LinkIcon sx={{ fontSize: 16 }} />}
                            disabled={copyLinks.loading}
                            onClick={() => copyLinks.callback(null)}>
                    Copy links
                </TinyButton>

                <IconButton size="small" onClick={props.onClose}>
                    <KeyboardDoubleArrowRight fontSize="small" />
                </IconButton>
            </Box>

            <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 3, py: 1 }}>
                <FieldRow label="Archive">
                    <Dropdown value={archive} options={archiveOptions} onChange={setArchive} />
                </FieldRow>

                <FieldRow label="Completed">
                    <Dropdown value={completed} options={completedOptions} onChange={setCompleted} />
                </FieldRow>

                <FieldRow label="Due date">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <BulkDueDateButton value={dueDate} onChange={setDueDate} />
                        {dueDate === undefined
                            ? <TinyButton onClick={() => setDueDate(null)}>Remove</TinyButton>
                            : <TinyButton onClick={() => setDueDate(undefined)}>Reset</TinyButton>}
                    </Box>
                </FieldRow>

                <FieldRow label="Project">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {project === null ? (
                                <Typography variant="caption" color="primary.main">No project</Typography>
                            ) : (
                                <TaskProject
                                    team={team}
                                    value={project ? project.id : null}
                                    goal={project ? project.goal : null}
                                    name={project ? project.name : ""}
                                    goalName={project ? project.goalName : ""}
                                    color={project ? project.color : ""}
                                    onChange={setProject}
                                />
                            )}
                        </Box>
                        {project === undefined
                            ? <TinyButton onClick={() => setProject(null)}>Remove</TinyButton>
                            : <TinyButton onClick={() => setProject(undefined)}>Reset</TinyButton>}
                    </Box>
                </FieldRow>

                <FieldRow label="Blocked by">
                    <BulkTaskLinks
                        placeholder="Wait on a task"
                        value={blockedBy}
                        onChange={setBlockedBy}
                        exclude={ids}
                    />
                </FieldRow>

                <FieldRow label="Followed by">
                    <BulkTaskLinks
                        placeholder="Add a task that waits on these"
                        value={followedBy}
                        onChange={setFollowedBy}
                        exclude={ids}
                    />
                </FieldRow>
            </Box>

            <Box sx={{
                display: "flex", justifyContent: "flex-end", gap: 1, p: 2,
                bgcolor: "grey.50",
                borderTop: 1, borderColor: "divider",
            }}>
                <Button onClick={props.onClose}>Cancel</Button>
                <Button variant="contained" disabled={!changed || apply.loading}
                        onClick={() => apply.callback(null)}>
                    Apply to {props.tasks.length} tasks
                </Button>
            </Box>
        </Box>
    );
}
